import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  TextField,
} from "@mui/material";
import { get } from "radash";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, UseControllerProps, useFormContext } from "react-hook-form";

import { getIsMobile } from "../../utils/helpers";

export type MultiSelectOption = {
  value: string;
  label: string;
};

export type MultiSelectProps = UseControllerProps<any> & {
  label: string;
  options: MultiSelectOption[];
  helperText?: string;
  includeOtherOption?: boolean;
  otherOptionLabel?: string;
  includeNoneOption?: boolean;
  noneOptionLabel?: string;
};

const MultiSelect: React.FC<MultiSelectProps> = ({
  control: parentControl,
  name,
  label,
  options,
  helperText,
  includeOtherOption = false,
  otherOptionLabel = "Other",
  includeNoneOption = false,
  noneOptionLabel = "None of these",
}) => {
  const isMobile = getIsMobile();

  const { control: contextControl, setValue, watch } = useFormContext();

  const optionValues = useMemo(() => new Set(options.map((opt) => opt.value)), [options]);

  return (
    <Controller
      name={name}
      control={parentControl}
      render={({ field, formState }) => {
        const error = get(formState.errors, name) as { message?: string } | undefined;
        const currentValue = Array.isArray(field.value) ? field.value : [];

        const initialOtherValue = useMemo(
          () => currentValue.find((v: string) => !optionValues.has(v) && v !== "None") || "",
          [],
        );

        const [isOtherChecked, setIsOtherChecked] = useState<boolean>(!!initialOtherValue);
        const [isNoneChecked, setIsNoneChecked] = useState<boolean>(currentValue.includes("None"));

        useEffect(() => {
          if (initialOtherValue) {
            setValue(`${name}.other`, initialOtherValue, {
              shouldValidate: false,
              shouldDirty: false,
            });
          }
          setIsOtherChecked(!!initialOtherValue);
        }, [initialOtherValue, name, setValue]);

        const otherTextValue = watch(`${name}.other`, "");

        const handleRegularChange = (optionValue: string, checked: boolean) => {
          const currentSelections = Array.isArray(field.value) ? field.value : [];
          let newValue: string[];

          if (checked) {
            // If a regular option is checked, make sure "None" is unchecked
            newValue = [...currentSelections.filter((v) => v !== "None"), optionValue];
            setIsNoneChecked(false);
          } else {
            newValue = currentSelections.filter((v: string) => v !== optionValue);
          }

          const currentOtherVal = isOtherChecked ? otherTextValue : null;
          const finalValue = options.map((opt) => opt.value).filter((v) => newValue.includes(v));

          if (
            currentOtherVal &&
            !finalValue.includes(currentOtherVal) &&
            currentOtherVal !== "None"
          ) {
            finalValue.push(currentOtherVal);
          }

          field.onChange(finalValue);
        };

        const handleNoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          const checked = event.target.checked;
          setIsNoneChecked(checked);

          if (checked) {
            // If "None" is selected, clear all other selections
            setIsOtherChecked(false);
            setValue(`${name}.other`, "", { shouldValidate: true });
            field.onChange(["None"]);
          } else {
            // If "None" is unselected, just remove it from the value
            const currentSelections = Array.isArray(field.value) ? field.value : [];
            field.onChange(currentSelections.filter((v) => v !== "None"));
          }
        };

        const handleOtherCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          const checked = event.target.checked;
          setIsOtherChecked(checked);

          const currentSelections = Array.isArray(field.value) ? field.value : [];
          // If "Other" is checked, uncheck "None"
          if (checked && currentSelections.includes("None")) {
            setIsNoneChecked(false);
          }

          let finalValue = currentSelections.filter(
            (v: string) => optionValues.has(v) && v !== "None",
          );

          if (checked) {
            if (otherTextValue && !finalValue.includes(otherTextValue)) {
              finalValue.push(otherTextValue);
            }
          } else {
            if (otherTextValue) {
              setValue(`${name}.other`, "", { shouldValidate: true });
            }
          }
          field.onChange(finalValue);
        };

        const size = isMobile ? "large" : "medium";
        return (
          <FormControl error={!!error} sx={{ width: "100%" }}>
            <FormLabel>{label}</FormLabel>
            <FormGroup sx={{ gap: { xs: 1, md: 0.5 } }}>
              {options.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      name={`${name}.${option.value}`}
                      size={size}
                      checked={currentValue.includes(option.value)}
                      onChange={(e) => handleRegularChange(option.value, e.target.checked)}
                      disabled={isNoneChecked}
                      sx={{ alignSelf: "flex-start" }}
                    />
                  }
                  label={option.label}
                />
              ))}

              {includeOtherOption && (
                <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                  <FormControlLabel
                    key="__other__"
                    control={
                      <Checkbox
                        size={size}
                        checked={isOtherChecked}
                        onChange={handleOtherCheckboxChange}
                        disabled={isNoneChecked}
                        sx={{ alignSelf: "flex-start" }}
                      />
                    }
                    label={otherOptionLabel}
                  />
                  {isOtherChecked && (
                    <Controller
                      name={`${name}.other`}
                      control={contextControl}
                      defaultValue={initialOtherValue}
                      render={({ field: otherField, fieldState: otherFieldState }) => {
                        const handleOtherTextChange = (
                          event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                        ) => {
                          const newText = event.target.value;
                          otherField.onChange(newText);

                          const currentSelections = Array.isArray(field.value) ? field.value : [];
                          let updatedValue = currentSelections.filter(
                            (v: string) => optionValues.has(v) && v !== "None",
                          );

                          if (newText) {
                            updatedValue.push(newText);
                          }

                          field.onChange(updatedValue);
                        };

                        return (
                          <TextField
                            {...otherField}
                            variant="outlined"
                            size="small"
                            label="Please specify"
                            fullWidth
                            onChange={handleOtherTextChange}
                            error={!!otherFieldState.error}
                            helperText={otherFieldState.error?.message}
                            disabled={isNoneChecked}
                            sx={{ my: 0 }}
                          />
                        );
                      }}
                    />
                  )}
                </Box>
              )}

              {includeNoneOption && (
                <FormControlLabel
                  key="__none__"
                  control={
                    <Checkbox
                      name={`${name}.__none__`}
                      size={size}
                      checked={isNoneChecked}
                      onChange={handleNoneChange}
                      sx={{ alignSelf: "flex-start" }}
                    />
                  }
                  label={noneOptionLabel}
                />
              )}
            </FormGroup>
            {(error?.message || helperText) && (
              <FormHelperText error={!!error}>{error?.message || helperText}</FormHelperText>
            )}
          </FormControl>
        );
      }}
    />
  );
};

export default MultiSelect;
